import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";

type Skill = {
  id: number;
  name: string;
  active: boolean;
  notes: string | null;
};

export function PeopleSkillsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("people");
  const readOnlyHint = "Your role has read-only access to the People module.";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");

  const skillsQuery = useQuery({
    queryKey: ["people", "skills"],
    queryFn: () => apiFetch<{ skills: Skill[] }>("/api/people/skills"),
  });

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["people", "skills"] });
    await queryClient.invalidateQueries({ queryKey: ["people", "training-matrix"] });
  }

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/people/skills", {
        method: "POST",
        body: { name, notes },
      }),
    onSuccess: async () => {
      setOpen(false);
      setName("");
      setNotes("");
      await refresh();
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const patchMutation = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      apiFetch(`/api/people/skills/${input.id}`, {
        method: "PATCH",
        body: input.body,
      }),
    onSuccess: refresh,
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const skills = skillsQuery.data?.skills ?? [];

  const columns: HubDataTableColumn<Skill>[] = [
    {
      id: "name",
      header: "Name",
      sortValue: (row) => row.name,
      cell: (row) => row.name,
    },
    {
      id: "notes",
      header: "Notes",
      sortValue: (row) => row.notes ?? "",
      cell: (row) => row.notes ?? "",
    },
    {
      id: "status",
      header: "Status",
      overflow: "nowrap",
      sortValue: (row) => (row.active ? 1 : 0),
      cell: (row) => (
        <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!writesAllowed || patchMutation.isPending}
            onClick={() => patchMutation.mutate({ id: row.id, body: { active: !row.active } })}
          >
            {row.active ? "Retire" : "Restore"}
          </Button>
        </ReadOnlyHoverLayer>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Skills are the columns on the training matrix. Retire a skill instead of deleting it so past qualifications still name it. Adding a skill does not mark anyone as trained.">
          Skills
        </PageTitleWithHelp>
        <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
          <Button type="button" disabled={!writesAllowed} onClick={() => setOpen(true)}>
            Add skill
          </Button>
        </ReadOnlyHoverLayer>
      </div>

      <HubDataTable
        title="Skill catalog"
        columns={columns}
        rows={skills}
        rowKey={(row) => row.id}
        loading={skillsQuery.isLoading}
        error={skillsQuery.isError ? (skillsQuery.error as Error).message : null}
        empty="No skills."
      />

      {open
        ? createPortal(
            <div {...hubModalOverlayProps()} role="dialog" aria-modal="true" aria-labelledby="add-skill-title">
              <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
                <h2 id="add-skill-title" className="text-base font-semibold">
                  Add skill
                </h2>
                <div className="mt-3 space-y-3">
                  <div>
                    <Label htmlFor="skill-name">Name</Label>
                    <Input id="skill-name" className="mt-1" value={name} onChange={(event) => setName(event.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="skill-notes">Notes</Label>
                    <Input id="skill-notes" className="mt-1" value={notes} onChange={(event) => setNotes(event.target.value)} />
                  </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={name.trim() === "" || createMutation.isPending}
                    onClick={() => createMutation.mutate()}
                  >
                    Add skill
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
